// The one-time explanation shown before the first HD / Ultra HD generation — what the tier gives, and what it costs — with a way to
// turn the box off for good (account setting hideHdInfo / hideUltraInfo). It is the same box the AI photo editor shows; the Studio
// Editor uses this shared copy.
import { useTranslation } from 'react-i18next';
import { IconCrown, IconSparkles, IconX } from '@tabler/icons-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useAuthStore } from '../../store/authStore';

/**
 * @param tier       'hd' | 'ultra' | null — the tier being explained (null = the box is closed)
 * @param onClose    () => void — close without generating
 * @param onProceed  (tier) => void — the user agreed: start the generation of that tier
 * @param ultraNote  optional line under the Ultra explanation (the AI photo editor states its passport output size there)
 */
export default function QualityInfoDialog({ tier, onClose, onProceed, ultraNote = null }) {
    const { t } = useTranslation();
    const { updateSettings } = useAuthStore();
    const ultra = tier === 'ultra';

    const proceed = async (dontShowAgain) => {
        const chosen = tier;
        if (dontShowAgain) {
            try { await updateSettings(ultra ? { hideUltraInfo: true } : { hideHdInfo: true }); } catch { /* not blocking */ }
        }
        onClose();
        onProceed(chosen);
    };

    return (
        <Dialog open={!!tier} onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent showClose={false} className="max-w-[560px] overflow-hidden rounded-2xl border border-zinc-200 p-0 font-hind dark:border-zinc-700">
                {tier && (
                    <>
                        <div className="flex items-center gap-3.5 border-b border-zinc-100 px-6 py-5 dark:border-zinc-800">
                            <span className={cn('flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white', ultra ? 'from-red-600 to-rose-700' : 'from-violet-600 to-indigo-600')}>
                                {ultra ? <IconCrown size={24} stroke={1.9} /> : <IconSparkles size={24} stroke={1.9} />}
                            </span>
                            <div className="min-w-0 flex-1">
                                <DialogTitle className="text-[19px] font-bold leading-snug text-zinc-900 dark:text-zinc-100">
                                    {t(ultra ? 'imageGeneration.quality.ultraModalTitle' : 'imageGeneration.quality.hdModalTitle')}
                                </DialogTitle>
                                <p className="mt-0.5 text-[13px] leading-snug text-zinc-400 dark:text-zinc-500">{t('imageGeneration.quality.modalSubtitle')}</p>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label={t('common.close')}
                                className="flex h-9 w-9 shrink-0 items-center justify-center self-center rounded-full bg-red-500 text-white transition-colors hover:bg-red-600"
                            >
                                <IconX size={18} stroke={2.5} />
                            </button>
                        </div>

                        <div className="px-6 py-5">
                            <DialogDescription className="text-[15px] leading-relaxed text-zinc-600 dark:text-zinc-300">
                                {t(ultra ? 'imageGeneration.quality.ultraModalBody' : 'imageGeneration.quality.hdModalBody')}
                            </DialogDescription>
                            {ultra && ultraNote && (
                                <div className="mt-4 flex items-center gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5 dark:border-amber-800 dark:bg-amber-900/20">
                                    <IconCrown size={19} className="shrink-0 text-amber-600 dark:text-amber-400" stroke={2} />
                                    <p className="text-[14px] !font-semibold leading-relaxed text-amber-700 dark:text-amber-300">{ultraNote}</p>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3 px-6 pb-6 pt-1">
                            <button
                                type="button"
                                onClick={() => proceed(true)}
                                className="h-[52px] shrink-0 rounded-xl border border-zinc-200 px-5 text-[14px] font-bold text-zinc-600 transition-colors hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
                            >
                                {t('imageGeneration.quality.okDontShow')}
                            </button>
                            <button
                                type="button"
                                onClick={() => proceed(false)}
                                className="inline-flex h-[52px] flex-1 items-center justify-center gap-2 rounded-xl bg-primary text-[16px] font-bold text-white shadow-md shadow-primary/30 transition-colors hover:bg-primary/90"
                            >
                                {t('imageGeneration.quality.ok')}
                            </button>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
